// src/components/admin/ManageModulesModal.tsx
import React, { useState } from "react";
import { useDeleteModuleMutation } from "../../services/courses";
import CreateModuleModal from "./CreateModuleModal";
import { EditModuleModal } from "./EditModuleModal";
import { ManageLessonsModal } from "./ManageLessonsModal";

interface Lesson {
  _id: string;
  title: string;
  description: string;
  videoPath: string;
  hasWorksheet: boolean;
  worksheetPath: string;
  duration?: number;
  order?: number;
}

interface Module {
  _id: string;
  title: string;
  description: string;
  order?: number;
  lessons: Lesson[];
}

interface Course {
  _id: string;
  title: string;
  description: string;
  published: boolean;
  modules: Module[];
}

interface ManageModulesModalProps {
  course: Course;
  onClose: () => void;
}

const ManageModulesModal: React.FC<ManageModulesModalProps> = ({
  course,
  onClose,
}) => {
  const [deleteModule] = useDeleteModuleMutation();

  const [showCreateModule, setShowCreateModule] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [managingModule, setManagingModule] = useState<Module | null>(null);

  const handleDeleteModule = async (moduleId: string, moduleTitle: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete module "${moduleTitle}"? All lessons in this module will also be deleted.`
      )
    ) {
      try {
        await deleteModule({ courseId: course._id, moduleId }).unwrap();
        alert("Module deleted successfully");
      } catch (err) {
        alert("Failed to delete module");
        console.error(err);
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const sortedModules = [...(course.modules || [])].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined)
      return a.order - b.order;
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    return 0;
  });

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {course.title}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Manage course modules and lessons
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4"
              >
                ×
              </button>
            </div>
            <button
              onClick={() => setShowCreateModule(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              + Add New Module
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {sortedModules.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No modules yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Get started by adding your first module to this course
                </p>
                <button
                  onClick={() => setShowCreateModule(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
                >
                  Create First Module
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedModules.map((module, index) => (
                  <div
                    key={module._id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Module Header */}
                    <div className="bg-gray-50 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                              Module {index + 1}
                            </span>
                            {module.order !== undefined && (
                              <span className="text-xs text-gray-500">
                                Order: {module.order}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {module.title}
                          </h3>
                          {module.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {module.description}
                            </p>
                          )}
                          <div className="mt-2 text-sm text-gray-500">
                            {module.lessons?.length || 0} lesson
                            {module.lessons?.length !== 1 ? "s" : ""}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Module Actions */}
                    <div className="p-4 bg-white">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setManagingModule(module)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Manage Lessons ({module.lessons?.length || 0})
                        </button>
                        <button
                          onClick={() => setEditingModule(module)}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteModule(module._id, module.title)
                          }
                          className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Lessons Preview */}
                    {module.lessons && module.lessons.length > 0 && (
                      <div className="px-4 pb-4 bg-white border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-2">
                          Recent lessons:
                        </p>
                        <div className="space-y-1">
                          {module.lessons.slice(0, 3).map((lesson) => (
                            <div
                              key={lesson._id}
                              className="text-sm text-gray-700 flex items-center gap-2"
                            >
                              <span className="text-gray-400">•</span>
                              <span className="truncate">{lesson.title}</span>
                              {lesson.duration && (
                                <span className="text-xs text-gray-500 ml-auto">
                                  {Math.floor(lesson.duration / 60)}:
                                  {String(lesson.duration % 60).padStart(
                                    2,
                                    "0"
                                  )}
                                </span>
                              )}
                            </div>
                          ))}
                          {module.lessons.length > 3 && (
                            <p className="text-xs text-gray-500 italic">
                              +{module.lessons.length - 3} more lesson
                              {module.lessons.length - 3 !== 1 ? "s" : ""}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      {showCreateModule && (
        <CreateModuleModal
          courseId={course._id}
          onClose={() => setShowCreateModule(false)}
        />
      )}

      {editingModule && (
        <EditModuleModal
          courseId={course._id}
          module={editingModule}
          onClose={() => setEditingModule(null)}
        />
      )}

      {managingModule && (
        <ManageLessonsModal
          courseId={course._id}
          module={managingModule}
          onClose={() => setManagingModule(null)}
        />
      )}
    </>
  );
};

export default ManageModulesModal;
